import React, { useMemo, useRef, useState } from 'react';
import ReactECharts from 'echarts-for-react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { getBaseRelationshipType } from '../utils/relationshipMappings';

const { FiMaximize2, FiZoomIn, FiZoomOut, FiInfo, FiMinimize2, FiX } = FiIcons;

const FamilyTreeView = ({ people, relationships, onPersonClick, focusedPersonId, onClose }) => {
  const echartsRef = useRef(null);
  const containerRef = useRef(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Filter people and relationships if focusedPersonId is provided
  const filteredData = useMemo(() => {
    if (!focusedPersonId) return { people, relationships };

    const directRelIds = new Set();
    directRelIds.add(focusedPersonId);

    // Get direct connections
    const directRels = relationships.filter(rel => {
      const isRelevant = rel.person_a_id === focusedPersonId || rel.person_b_id === focusedPersonId;
      if (isRelevant) {
        directRelIds.add(rel.person_a_id);
        directRelIds.add(rel.person_b_id);
      }
      return isRelevant;
    });

    // Also include 2nd degree connections for context (optional but better for "family tree")
    // For now, let's stick to 1st degree + spouses of 1st degree as requested "only show relationships of that person"
    const filteredPeople = people.filter(p => directRelIds.has(p.id));
    
    return { 
      people: filteredPeople, 
      relationships: directRels 
    };
  }, [people, relationships, focusedPersonId]);

  const chartOptions = useMemo(() => {
    const nodes = filteredData.people.map(person => ({
      id: person.id,
      name: person.name,
      symbolSize: person.id === focusedPersonId ? 75 : 60,
      value: person.proximity,
      category: person.sex === 'male' ? 0 : person.sex === 'female' ? 1 : 2,
      itemStyle: {
        borderColor: person.id === focusedPersonId ? '#4f46e5' : (person.is_family ? '#6366f1' : '#94a3b8'),
        borderWidth: person.id === focusedPersonId ? 5 : (person.is_family ? 3 : 1),
        shadowBlur: 10,
        shadowColor: 'rgba(0,0,0,0.1)'
      },
      label: {
        show: true,
        position: 'bottom',
        formatter: person.name,
        color: '#1e293b',
        fontWeight: person.id === focusedPersonId ? 'bold' : 'normal',
        fontSize: person.id === focusedPersonId ? 14 : 12
      }
    }));

    const links = filteredData.relationships.map(rel => {
      // Use getBaseRelationshipType to consistently map terms like 'father' -> 'parent', 'wife' -> 'spouse'
      const baseType = getBaseRelationshipType(rel.relationship_type || '');
      const baseTypeB = getBaseRelationshipType(rel.relationship_type_b || '');
      
      const isMarriage = baseType.includes('spouse') || baseType.includes('in_law') || 
                         baseTypeB.includes('spouse') || baseTypeB.includes('in_law');
      
      const isStep = baseType.includes('step') || baseTypeB.includes('step');
      
      // If it's a direct family tie, not marriage and not step, consider it bloodline
      const isBloodline = !isMarriage && !isStep && (
        baseType.includes('parent') || 
        baseType.includes('child') || 
        baseType.includes('sibling') || 
        baseType.includes('cousin') || 
        baseType.includes('grand') || 
        baseType.includes('removed') ||
        baseTypeB.includes('parent') || 
        baseTypeB.includes('child') || 
        baseTypeB.includes('sibling') || 
        baseTypeB.includes('cousin') || 
        baseTypeB.includes('grand') || 
        baseTypeB.includes('removed')
      );

      return {
        source: rel.person_a_id,
        target: rel.person_b_id,
        label: {
          show: false,
          formatter: rel.relationship_type_b
        },
        lineStyle: {
          color: isMarriage ? '#f43f5e' : isBloodline ? '#10b981' : '#94a3b8',
          width: isMarriage ? 3 : 2,
          type: isMarriage ? 'solid' : 'dashed',
          curveness: 0.1
        }
      };
    });

    return {
      tooltip: {
        trigger: 'item',
        formatter: (params) => {
          if (params.dataType === 'node') {
            const person = people.find(p => p.id === params.data.id);
            return `
              <div style="padding: 8px;">
                <b style="font-size: 14px;">${person.name}</b><br/>
                <span style="color: #666;">${person.sex} | ${person.proximity} Proximity</span><br/>
                ${person.nicknames ? `<i style="font-size: 11px;">"${person.nicknames}"</i>` : ''}
              </div>
            `;
          }
          return `Connection: <b>${params.data.label.formatter || 'Related'}</b>`;
        }
      },
      legend: [{
        data: ['Male', 'Female', 'Other'],
        bottom: 10,
        left: 'center'
      }],
      series: [
        {
          type: 'graph',
          layout: 'force',
          animation: true,
          draggable: true,
          data: nodes,
          links: links,
          categories: [
            { name: 'Male', itemStyle: { color: '#3b82f6' } },
            { name: 'Female', itemStyle: { color: '#ec4899' } },
            { name: 'Other', itemStyle: { color: '#8b5cf6' } }
          ],
          roam: true,
          label: {
            show: true,
            position: 'right'
          },
          force: {
            repulsion: 800,
            edgeLength: 200,
            gravity: 0.1
          },
          emphasis: {
            focus: 'adjacency',
            lineStyle: {
              width: 5
            }
          }
        }
      ]
    };
  }, [filteredData, people, focusedPersonId]);

  const handleZoomIn = () => {
    const echartInstance = echartsRef.current.getEchartsInstance();
    const option = echartInstance.getOption();
    const zoom = option.series[0].zoom || 1;
    echartInstance.setOption({
      series: [{
        zoom: zoom * 1.2
      }]
    });
  };

  const handleZoomOut = () => {
    const echartInstance = echartsRef.current.getEchartsInstance();
    const option = echartInstance.getOption();
    const zoom = option.series[0].zoom || 1;
    echartInstance.setOption({
      series: [{
        zoom: zoom / 1.2
      }]
    });
  };

  const handleToggleMaximize = () => {
    if (!document.fullscreenElement) {
      containerRef.current.requestFullscreen().catch(err => {
        console.error(`Error attempting to enable fullscreen: ${err.message}`);
      });
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  const onEvents = {
    'click': (params) => {
      if (params.dataType === 'node') {
        const person = people.find(p => p.id === params.data.id);
        if (person) onPersonClick(person);
      }
    }
  };

  return (
    <div 
      ref={containerRef}
      className={`bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden relative ${isFullscreen ? 'w-full h-full' : ''}`} 
      style={{ height: isFullscreen ? '100vh' : '70vh' }}
    >
      <div className="absolute top-4 left-4 z-10 space-y-2">
        {focusedPersonId && (
          <div className="bg-blue-600 text-white px-4 py-2 rounded-2xl shadow-lg flex items-center space-x-2">
            <SafeIcon icon={FiInfo} size={14} />
            <span className="text-xs font-bold">
              Focusing on {people.find(p => p.id === focusedPersonId)?.name}'s Tree
            </span>
          </div>
        )}
        <div className="bg-white/80 backdrop-blur-md p-3 rounded-2xl shadow-sm border border-gray-100 flex items-center space-x-3">
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 rounded-full bg-emerald-500" />
            <span className="text-xs font-semibold text-gray-600">Bloodline</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 rounded-full bg-rose-500" />
            <span className="text-xs font-semibold text-gray-600">Marriage</span>
          </div>
        </div>
      </div>

      {onClose && (
        <div className="absolute top-4 right-4 z-10">
          <button 
            onClick={onClose}
            className="p-3 bg-white hover:bg-red-50 text-red-600 rounded-xl shadow-md border border-gray-100 transition-all active:scale-95"
            title="Close Tree View"
          >
            <SafeIcon icon={FiX} size={18} />
          </button>
        </div>
      )}

      <ReactECharts 
        ref={echartsRef}
        option={chartOptions} 
        style={{ height: '100%', width: '100%' }} 
        onEvents={onEvents}
      />
      
      <div className="absolute bottom-4 right-4 flex space-x-2 z-10">
        <button 
          onClick={handleZoomIn}
          className="p-3 bg-white hover:bg-blue-50 text-blue-600 rounded-xl shadow-md border border-gray-100 transition-all active:scale-95"
          title="Zoom In"
        >
          <SafeIcon icon={FiZoomIn} size={18} />
        </button>
        <button 
          onClick={handleZoomOut}
          className="p-3 bg-white hover:bg-blue-50 text-blue-600 rounded-xl shadow-md border border-gray-100 transition-all active:scale-95"
          title="Zoom Out"
        >
          <SafeIcon icon={FiZoomOut} size={18} />
        </button>
        <button 
          onClick={handleToggleMaximize}
          className="p-3 bg-white hover:bg-blue-50 text-blue-600 rounded-xl shadow-md border border-gray-100 transition-all active:scale-95"
          title={isFullscreen ? "Exit Fullscreen" : "Maximize"}
        >
          <SafeIcon icon={isFullscreen ? FiMinimize2 : FiMaximize2} size={18} />
        </button>
      </div>
    </div>
  );
};

export default FamilyTreeView;